import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { LegalDisclaimer } from "../components/LegalDisclaimer";
import { Metric } from "../components/Metric";
import { StatusStamp } from "../components/StatusStamp";
import { formatDate, formatMoney } from "../lib/format";
import { DEBTOR_LABELS } from "../lib/obligations";
import {
  fetchSummary,
  obligationHref,
  type LimitationRow,
  type Summary,
  type SummaryRow,
  type SummarySide,
} from "../lib/summary";

/**
 * Сводка руководителя (E12, ТЗ 4.2.6) — стартовый экран директора, юриста, коммерческого отдела
 * и бухгалтерии. Делопроизводителю раздел не показывается, и сервер ему отказывает
 * (строка 4.3 «Сводка руководителя, эскалации»).
 *
 * Пять показателей и два списка — по перечню ТЗ. Ни одна цифра здесь не считается: всё приходит
 * готовым с сервера, теми же функциями, что считают карточку договора. Порядок строк — тоже
 * серверный (по критичности / по близости истечения давности), клиент его не пересортировывает.
 *
 * Графики и дашборд — отдельная задача (E12a), выгрузки в Excel — второй этап.
 */

const cellCls = "px-2 py-1.5 align-top";
const headCls = "px-2 py-1.5 text-left text-[12px] font-bold text-muted";

export function SummaryPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const res = await fetchSummary();
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setSummary(res.value);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null) {
    return (
      <Shell>
        <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
          {error}
        </p>
        <button
          type="button"
          className="mt-3 rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg"
          onClick={() => void load()}
        >
          Повторить
        </button>
      </Shell>
    );
  }

  if (summary === null) {
    return (
      <Shell>
        <p role="status" className="mt-3 text-[13px] text-muted">
          Загрузка…
        </p>
      </Shell>
    );
  }

  const { metrics } = summary;

  return (
    <Shell>
      <p className="mt-1 text-[13px] text-muted">Состояние на {formatDate(summary.today)}.</p>

      {/*
        Раскладка С2 (CR-5, пакет 3; docs/29 §4.1): плитки колонкой слева, таблица эскалаций
        справа — на широком экране. Концепция docs/07 §4 требует от сводки измеримого:
        «4–6 метрик + список эскалаций, читается за 30 секунд». Лентой это не выполнялось —
        чтобы увидеть эскалации, приходилось прокручивать, а после прокрутки уже не видно
        цифр. Рядом директор видит «12 эскалаций» и сами двенадцать строк одним взглядом.

        До ширины 2xl (ноутбук) остаётся прежняя лента: две области по 400 и 1176 точек там
        не помещаются, и плитки стали бы у́же читаемого.
      */}
      <div className="mt-4 grid gap-4 2xl:grid-cols-[minmax(0,1fr)_minmax(0,3fr)] 2xl:items-start">
        {/* Ступень xl:3 сохранена намеренно: на ноутбучных ширинах (1280–1535) раскладка
            в две области ещё не включается, и без неё плиток в ряду стало бы две вместо трёх —
            лента выросла бы на целый ряд, а эскалации уехали бы ниже. */}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-1">
          <Metric
            title="Обязательств на контроле"
            value={metrics.onControl.total}
            caption={`в т.ч. ожидают события: ${metrics.onControl.awaiting}`}
            hint="Всё, кроме исполненного и снятого с контроля."
          />
          <SideMetric
            title="Требования к контрагентам"
            side={metrics.claimsToThem}
            debtor="they"
            hint="Начислено по просрочкам контрагентов на сегодня."
          />
          <SideMetric
            title="Наши риски (экспозиция)"
            side={metrics.exposure}
            debtor="we"
            hint="Сколько контрагенты потенциально могут предъявить нам по нашим просрочкам."
            risk
          />
          <Metric
            title="Ближайшие сроки"
            value={metrics.horizon.days7}
            caption={`в ближайшие 7 дней · за 14 дней: ${metrics.horizon.days14}`}
            hint="Сроки, которые ещё не наступили. Окно в 14 дней включает ближайшие 7."
          />
          <Metric
            title="Эскалации"
            value={metrics.escalated}
            caption="просрочек сверх порога"
            hint="По ним ушёл сигнал директору и юристу."
            risk={metrics.escalated > 0}
          />
        </div>

        {/* Правая область: сама таблица эскалаций и подпись под ней. Отступ сверху у блока
            снимается — в две области он оторвал бы таблицу от плиток по вертикали. */}
        <div>
          <RowsBlock
            id="summary-escalations"
            title="Эскалации: просрочки, требующие решения"
            empty="Просрочек сверх порога нет."
            rows={summary.escalations}
            flush
          />
          <p className="mt-2 text-[13px]">
            {/* Обрезка списка называется прямо: молча показанные «первые 50 из 300» читались бы как
            «эскалаций всего 50», а это в системе контроля сроков опасное заблуждение. */}
            {summary.escalationsShown < metrics.escalated && (
              <span className="mr-2 text-muted">
                Показаны первые {summary.escalationsShown} из {metrics.escalated}.
              </span>
            )}
            <Link className="text-accent hover:underline" to="/obligations?status=overdue">
              Все просрочки в реестре обязательств
            </Link>
          </p>
        </div>
      </div>

      {/* Исковая давность и правовая оговорка — колонкой текста (--w-text ≈ 70 знаков):
          это сплошной текст, а не таблица, и во всю ширину рабочей области строка доходила бы
          до ~150 знаков — вдвое выше нормы читаемости (решение владельца 07.09.2026).
          Ограничение стоит ЗДЕСЬ, а не внутри LegalDisclaimer: тот же компонент печатается в
          составе расчёта и претензии, где ширину задаёт лист. */}
      <div className="max-w-[var(--w-text)]">
        <LimitationBlock rows={summary.limitation} />

        <LegalDisclaimer />
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section aria-labelledby="summary-title">
      <h1 id="summary-title" className="text-[20px] font-bold text-ink">
        Сводка
      </h1>
      {children}
    </section>
  );
}

/**
 * Плитка контура «должник — контрагент / должник — мы». Главное число — НАЧИСЛЕННЫЕ САНКЦИИ;
 * под ним обязательно и то, сколько обязательств их набрало, и сколько просрочено СЕЙЧАС: в счёт
 * входит и погашенное с опозданием, и без этой строки цифра читалась бы как «сейчас должны N».
 *
 * Ссылка в реестр повешена ИМЕННО на «сейчас просрочено»: у этой цифры в реестре есть точный
 * отбор (сторона-должник + статус «Просрочено»), и открытый список совпадёт с ней строка в строку.
 * На «обязательств с начисленными санкциями» ссылки нет — такого фильтра ТЗ 4.2.6 не знает, и
 * переход показал бы другое число, чем плитка.
 */
function SideMetric({
  title,
  side,
  debtor,
  hint,
  risk = false,
}: {
  title: string;
  side: SummarySide;
  debtor: "we" | "they";
  hint: string;
  risk?: boolean;
}) {
  return (
    <Metric
      title={title}
      value={formatMoney(side.totals.sanctions)}
      caption={
        <>
          обязательств с начисленными санкциями: {side.totals.count} ·{" "}
          <Link
            className="text-accent hover:underline"
            to={`/obligations?status=overdue&debtor=${debtor}`}
          >
            сейчас просрочено: {side.overdueNow}
          </Link>
          <br />
          остаток долга {formatMoney(side.totals.debt)}
        </>
      }
      hint={hint}
      risk={risk}
    />
  );
}

/** Таблица строк обязательств: договор, контрагент, обязательство, срок, статус. */
function RowsBlock({
  id,
  title,
  empty,
  rows,
  flush = false,
}: {
  id: string;
  title: string;
  empty: string;
  rows: readonly SummaryRow[];
  /** Без верхнего отступа: блок стои́т рядом с плитками, а не под ними (раскладка С2). */
  flush?: boolean;
}) {
  return (
    <section className={flush ? "" : "mt-6"} aria-labelledby={id}>
      <h2 id={id} className="text-[16px] font-bold text-ink">
        {title}
      </h2>
      {rows.length === 0 ? (
        <p className="mt-1 text-[13px] text-muted">{empty}</p>
      ) : (
        <div className="mt-2">
          <table className="sticky-head sticky-head--on-bg w-full border-collapse text-[13px]">
            <caption className="sr-only">
              {title}: договор, контрагент, обязательство, срок, просрочка, статус
            </caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className={headCls}>
                  Договор
                </th>
                <th scope="col" className={headCls}>
                  Контрагент
                </th>
                <th scope="col" className={headCls}>
                  Обязательство
                </th>
                <th scope="col" className={headCls}>
                  Срок
                </th>
                <th scope="col" className={headCls}>
                  Просрочка
                </th>
                <th scope="col" className={headCls}>
                  Статус
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.obligationId} className="border-b border-border-soft">
                  <td className={cellCls}>
                    <Link className="text-accent hover:underline" to={obligationHref(row)}>
                      {row.contractNumber}
                    </Link>
                  </td>
                  <td className={cellCls}>{row.counterpartyName}</td>
                  <td className={cellCls}>
                    {row.descr}
                    <span className="mt-0.5 block text-[11px] text-muted">
                      {/* Термин — из общего словаря (правило 8): в реестре он тот же. */}
                      {DEBTOR_LABELS[row.debtor]} · {formatMoney(row.remaining ?? row.amount)}
                    </span>
                  </td>
                  <td className={`${cellCls} num whitespace-nowrap`}>{formatDate(row.due)}</td>
                  <td className={`${cellCls} num whitespace-nowrap`}>
                    {row.overdue > 0 ? `${row.overdue} дн.` : "—"}
                  </td>
                  <td className={cellCls}>
                    <StatusStamp status={row.status} escalated={row.escalated} short />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Реестр контроля исковой давности (ТЗ 4.2.5), по близости истечения. Истёкшие идут первыми и
 * помечены явно: пропущенный срок необратим, и он не должен теряться среди «осталось полгода».
 */
function LimitationBlock({ rows }: { rows: readonly LimitationRow[] }) {
  return (
    <section className="mt-6" aria-labelledby="summary-limitation">
      <h2 id="summary-limitation" className="text-[16px] font-bold text-ink">
        Исковая давность: истекает в ближайшие полгода
      </h2>
      {rows.length === 0 ? (
        <p className="mt-1 text-[13px] text-muted">
          Требований с истекающей исковой давностью нет.
        </p>
      ) : (
        <ul className="mt-2 space-y-1 text-[13px]">
          {rows.map((row) => (
            <li key={row.obligationId}>
              <Link className="text-accent hover:underline" to={obligationHref(row)}>
                {row.contractNumber}
              </Link>{" "}
              — {row.descr} ({row.counterpartyName}): давность истекает{" "}
              <span className="num">{formatDate(row.limitDate)}</span>
              {row.limitExpired ? (
                <span className="stamp ml-1 bg-overdue-bg text-overdue-fg">срок истёк</span>
              ) : (
                ` — осталось ${row.limitDaysLeft ?? 0} дн.`
              )}
              {row.hasActiveClaim && (
                <span
                  className="ml-1 text-[12px] text-muted"
                  title="Течение давности может быть приостановлено на срок досудебной процедуры (п. 3 ст. 202 ГК РФ) — оценивает юрист"
                >
                  · направлена претензия
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
